import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Skeleton,
  Alert,
  Snackbar,
  Link,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ComposedChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid, ReferenceDot, Legend } from 'recharts';
import MuiTooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import { getStockResearch, StockResearchResponse, DCFAnalysis, getSavedReports, getSavedReport, deleteSavedReport, SavedReportSummary, API_BASE_URL, getAnalystTargets, FinvizAnalystTarget } from '../services/api';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { useNavigate } from 'react-router-dom';
import { bloomberg } from '../theme/bloombergTheme';
import { getTickers, saveTickers, getWatchlist, saveWatchlist } from '../utils/portfolioStorage';

const Research = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateTo = useNavigate();
  const [ticker, setTicker] = useState('');
  const [searchOptions, setSearchOptions] = useState<{ symbol: string; name: string; country: string }[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [research, setResearch] = useState<StockResearchResponse | null>(null);
  const [actionSnackbar, setActionSnackbar] = useState<string | null>(null);
  const [fsView, setFsView] = useState<'annual' | 'quarterly'>('annual');
  const [fsTab, setFsTab] = useState<'income_statement' | 'balance_sheet' | 'cash_flow'>('income_statement');
  const [savedReports, setSavedReports] = useState<SavedReportSummary[]>([]);
  const [reportSearch, setReportSearch] = useState('');
  const [reportSectorFilter, setReportSectorFilter] = useState('');
  const [reportRatingFilter, setReportRatingFilter] = useState('');
  const [showMA20, setShowMA20] = useState(true);
  const [showMA50, setShowMA50] = useState(true);
  const [showMA200, setShowMA200] = useState(true);
  const chartRef = useRef<HTMLDivElement>(null);
  const [comps, setComps] = useState<any>(null);
  const [compsLoading, setCompsLoading] = useState(false);
  const [analystTargets, setAnalystTargets] = useState<FinvizAnalystTarget[]>([]);
  const [analystTargetsLoading, setAnalystTargetsLoading] = useState(false);
  const deepLinkHandled = useRef(false);

  useEffect(() => {
    loadSavedReports();
  }, []);

  const doSearch = useCallback(async (sym: string) => {
    setTicker(sym);
    setLoading(true);
    setError(null);
    try {
      const data = await getStockResearch(sym);
      setResearch(data);
      loadSavedReports();
    } catch (err: any) {
      console.error('Error fetching research:', err);
      setError(err.response?.data?.detail || 'Failed to generate research report. Please try again.');
      setResearch(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Deep-link: auto-load symbol from ?symbol= query param
  useEffect(() => {
    const symbolParam = searchParams.get('symbol');
    if (symbolParam && !deepLinkHandled.current) {
      deepLinkHandled.current = true;
      setSearchParams({}, { replace: true });
      doSearch(symbolParam.toUpperCase());
    }
  }, [searchParams, setSearchParams, doSearch]);

  // Fetch comparable companies when a research report is loaded
  useEffect(() => {
    if (research?.symbol) {
      setCompsLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
      fetch(`${API_BASE_URL}/api/research/${research.symbol}/comps`)
        .then(res => res.json())
        .then(data => setComps(data))
        .catch(() => setComps(null))
        .finally(() => setCompsLoading(false));
    }
  }, [research?.symbol]);

  // Fetch per-analyst price targets when a research report is loaded
  useEffect(() => {
    if (research?.symbol) {
      setAnalystTargets([]);
      setAnalystTargetsLoading(true);
      getAnalystTargets(research.symbol)
        .then(data => setAnalystTargets(data.targets || []))
        .catch(() => setAnalystTargets([]))
        .finally(() => setAnalystTargetsLoading(false));
    }
  }, [research?.symbol]);

  const loadSavedReports = async () => {
    try {
      const reports = await getSavedReports();
      setSavedReports(reports);
    } catch (err) {
      console.error('Error loading saved reports:', err);
    }
  };

  const handleSearch = async () => {
    if (!ticker.trim()) {
      setError('Please enter a ticker symbol');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getStockResearch(ticker.toUpperCase());
      setResearch(data);
      loadSavedReports(); // Refresh saved reports list
    } catch (err: any) {
      console.error('Error fetching research:', err);
      setError(err.response?.data?.detail || 'Failed to generate research report. Please try again.');
      setResearch(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSaved = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSavedReport(id);
      setResearch(data);
    } catch (err: any) {
      setError('Failed to load saved report.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteSavedReport(id);
      setSavedReports(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error('Error deleting report:', err);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const downloadReport = async () => {
    if (!research) return;

    try {
      const jsPDFModule: any = await import('jspdf');
      const html2canvasModule: any = await import('html2canvas');
      const html2canvas = html2canvasModule.default || html2canvasModule;
      const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default || jsPDFModule;
      const doc = new jsPDF();

      // Capture chart as image before generating PDF
      let chartImgData: string | null = null;
      if (chartRef.current) {
        try {
          const canvas = await html2canvas(chartRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
            logging: false,
          });
          chartImgData = canvas.toDataURL('image/png');
        } catch (chartErr) {
          console.warn('Chart capture failed:', chartErr);
        }
      }
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 15;
      const contentW = W - M * 2;
      let y = 0;

      // ── Currency symbols from backend ─────────────────────────────────
      const tSym = (research as any).trading_symbol || '$';   // price symbol (e.g. "p" for GBp)
      const fSym = (research as any).financial_symbol || '$'; // financial symbol (e.g. "£" for GBP)

      // ── Helpers ──────────────────────────────────────────────────────
      const fmtBig = (n: number | null | undefined, sym: string = fSym) => {
        if (n == null) return 'N/A';
        if (Math.abs(n) >= 1e12) return `${sym}${(n / 1e12).toFixed(2)}T`;
        if (Math.abs(n) >= 1e9) return `${sym}${(n / 1e9).toFixed(1)}B`;
        if (Math.abs(n) >= 1e6) return `${sym}${(n / 1e6).toFixed(0)}M`;
        return `${sym}${n.toLocaleString()}`;
      };
      const pct = (n: number | null | undefined) => n != null ? `${(n * 100).toFixed(1)}%` : 'N/A';
      const f2 = (n: number | null | undefined) => n != null ? n.toFixed(2) : 'N/A';
      const safe = (v: any) => (v != null && v !== '') ? String(v) : 'N/A';

      const newPageIfNeeded = (need: number) => {
        if (y + need > H - 14) { doc.addPage(); y = M; }
      };

      const para = (txt: string, x: number, maxW: number, size = 8, style = 'normal', color = '#333333') => {
        if (!txt) return;
        doc.setFontSize(size);
        doc.setFont('helvetica', style);
        doc.setTextColor(color);
        const lineH = size * 0.42;
        const lines: string[] = doc.splitTextToSize(txt, maxW);
        for (const line of lines) {
          newPageIfNeeded(lineH + 1);
          doc.text(line, x, y);
          y += lineH;
        }
      };

      const heading = (title: string) => {
        newPageIfNeeded(12);
        y += 4;
        doc.setDrawColor(26, 35, 126);
        doc.setLineWidth(0.4);
        doc.line(M, y, W - M, y);
        y += 5;
        doc.setFontSize(9.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor('#1a237e');
        doc.text(title.toUpperCase(), M, y);
        y += 5;
      };

      const subheading = (title: string) => {
        newPageIfNeeded(8);
        y += 2;
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor('#333333');
        doc.text(title, M, y);
        y += 4;
      };

      const metricGrid = (items: [string, string][], cols = 3) => {
        const colW = contentW / cols;
        let col = 0;
        for (const [label, value] of items) {
          const x = M + col * colW;
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor('#888888');
          doc.text(label, x, y);
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor('#111111');
          doc.text(value, x, y + 4);
          col++;
          if (col >= cols) {
            col = 0;
            y += 9;
            newPageIfNeeded(10);
          }
        }
        if (col > 0) y += 9;
      };

      // Draw a simple table from headers + rows
      const drawTable = (headers: string[], rows: string[][], colWidths?: number[]) => {
        const numCols = headers.length;
        const cw = colWidths || headers.map(() => contentW / numCols);
        // Header
        newPageIfNeeded(8);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor('#1a237e');
        let cx = M;
        for (let i = 0; i < numCols; i++) {
          const textX = i === 0 ? cx : cx + cw[i];
          doc.text(headers[i], textX, y, { align: i === 0 ? 'left' : 'right', maxWidth: cw[i] - 2 });
          cx += cw[i];
        }
        y += 1;
        doc.setDrawColor('#cccccc');
        doc.setLineWidth(0.3);
        doc.line(M, y, W - M, y);
        y += 3;
        // Rows
        for (let ri = 0; ri < rows.length; ri++) {
          const row = rows[ri];
          newPageIfNeeded(5);
          // Alternating row shading
          if (ri % 2 === 1) {
            doc.setFillColor(248, 248, 252);
            doc.rect(M, y - 2.5, contentW, 3.5, 'F');
          }
          cx = M;
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor('#333333');
          for (let i = 0; i < numCols; i++) {
            const textX = i === 0 ? cx : cx + cw[i];
            doc.text(row[i] || '', textX, y, { align: i === 0 ? 'left' : 'right', maxWidth: cw[i] - 2 });
            cx += cw[i];
          }
          y += 3.5;
        }
        y += 1;
      };

      // ── Data references ──────────────────────────────────────────────
      const ks = (research.key_stats || {}) as any;
      const fund = (research.fundamentals || {}) as any;
      const val = fund.valuation || {};
      const prof = fund.profitability || {};
      const gr = fund.growth || {};
      const fh = fund.financial_health || {};
      const pt = (research.price_targets || {}) as any;
      const ar = (research.analyst_recommendations || {}) as any;
      const dcf = research.dcf_analysis;
      const swot = research.cfa_analysis?.swot;
      const cfa = research.cfa_analysis;
      const peer = cfa?.peer_comparison as any;

      // ================================================================
      // HEADER BAR
      // ================================================================
      doc.setFillColor(26, 35, 126);
      doc.rect(0, 0, W, 24, 'F');
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor('#FFFFFF');
      doc.text(research.symbol, M, 15);
      const symW = doc.getTextWidth(research.symbol + '  ');
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(research.company_name || '', M + symW, 15);
      if (research.current_price) {
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(`${tSym}${research.current_price.toFixed(2)}`, W - M, 11, { align: 'right' });
        if (research.price_change != null) {
          const sign = research.price_change >= 0 ? '+' : '';
          doc.setFontSize(9);
          doc.setTextColor(research.price_change >= 0 ? '#a5d6a7' : '#ef9a9a');
          doc.text(`${sign}${research.price_change.toFixed(2)}%`, W - M, 18, { align: 'right' });
        }
      }
      y = 28;
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor('#888888');
      const dateFmt = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      doc.text([research.sector, dateFmt].filter(Boolean).join('  |  '), M, y);
      if (research.recommendation?.rating) {
        const r = research.recommendation.rating;
        const c = r === 'BUY' ? '#2e7d32' : r === 'SELL' ? '#c62828' : '#e65100';
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(c);
        doc.text(r, W - M, y, { align: 'right' });
      }
      y = 34;

      // ================================================================
      // KEY METRICS BOX
      // ================================================================
      doc.setFillColor(247, 247, 252);
      doc.roundedRect(M, y, contentW, 38, 1.5, 1.5, 'F');
      y += 5;
      metricGrid([
        ['Market Cap', ks.market_cap || fmtBig(val.market_cap)],
        ['P/E (TTM)', f2(val.pe_ratio)],
        ['Forward P/E', f2(val.forward_pe)],
        ['Price Target', pt.mean ? `${tSym}${pt.mean.toFixed(0)}` : 'N/A'],
        ['Upside', pt.upside != null ? `${pt.upside >= 0 ? '+' : ''}${pt.upside.toFixed(0)}%` : 'N/A'],
        ['Beta', ks.beta || 'N/A'],
        ['Revenue (TTM)', fmtBig(prof.revenue_ttm)],
        ['Net Income', fmtBig(prof.net_income_ttm)],
        ['Gross Margin', pct(prof.gross_margin)],
        ['Op. Margin', pct(prof.operating_margin)],
        ['Profit Margin', pct(prof.profit_margin)],
        ['D/E Ratio', fh.debt_to_equity != null ? fh.debt_to_equity.toFixed(1) : 'N/A'],
      ], 3);
      y = 34 + 38 + 3;

      // ================================================================
      // EXECUTIVE SUMMARY (full, no truncation)
      // ================================================================
      if (research.ai_summary) {
        heading('Executive Summary');
        para(research.ai_summary, M, contentW, 8);
        y += 2;
      }

      // ================================================================
      // KEY INSIGHTS (all)
      // ================================================================
      if (research.key_insights?.length) {
        heading('Key Insights');
        for (const insight of research.key_insights) {
          newPageIfNeeded(6);
          para(`\u2022  ${insight}`, M + 1, contentW - 2, 7.5);
          y += 1;
        }
        y += 1;
      }

      // ================================================================
      // ANALYST RATINGS + PRICE TARGETS
      // ================================================================
      if (ar.has_data) {
        heading('Analyst Ratings');
        const bd = ar.breakdown || {};
        const total = ar.num_analysts || 1;
        const barData: [string, number, string][] = [
          ['Strong Buy', bd.strong_buy || 0, '#1b5e20'],
          ['Buy', bd.buy || 0, '#4caf50'],
          ['Hold', bd.hold || 0, '#ffa000'],
          ['Sell', bd.sell || 0, '#e53935'],
          ['Strong Sell', bd.strong_sell || 0, '#b71c1c'],
        ];
        newPageIfNeeded(30);
        for (const [label, count, color] of barData) {
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor('#555555');
          doc.text(label, M, y);
          const barX = M + 28;
          const maxBarW = 50;
          const barW = Math.max(1, (count / total) * maxBarW);
          doc.setFillColor(color);
          doc.roundedRect(barX, y - 2.8, barW, 3.2, 1, 1, 'F');
          doc.setFontSize(7);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor('#333333');
          doc.text(String(count), barX + barW + 2, y);
          y += 4.5;
        }
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor('#666666');
        doc.text(`Consensus: ${ar.consensus || 'N/A'}  |  ${ar.num_analysts} analysts  |  Trend: ${ar.recommendation_trend || 'N/A'}`, M, y);
        y += 4;

        if (pt.mean != null) {
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor('#1a237e');
          doc.text('Price Targets:', M, y);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor('#333333');
          doc.text(`Mean ${tSym}${pt.mean.toFixed(0)}  |  High ${tSym}${pt.high?.toFixed(0) || 'N/A'}  |  Low ${tSym}${pt.low?.toFixed(0) || 'N/A'}  |  Upside ${pt.upside != null ? (pt.upside >= 0 ? '+' : '') + pt.upside.toFixed(0) + '%' : 'N/A'}`, M + 28, y);
          y += 3;
        }
      }

      // Perplexity analyst consensus narrative
      if (cfa?.analyst_consensus) {
        if (!ar.has_data) heading('Analyst Consensus');
        else { y += 2; subheading('Analyst Consensus Commentary'); }
        para(cfa.analyst_consensus, M, contentW, 7.5);
        y += 2;
      }

      // ================================================================
      // BALANCE SHEET ANALYSIS (full narrative + table)
      // ================================================================
      if (cfa?.balance_sheet) {
        heading('Balance Sheet Analysis');
        para(cfa.balance_sheet, M, contentW, 7.5);
        y += 2;
        if (cfa.balance_sheet_table?.length) {
          drawTable(
            ['Metric', 'Value'],
            cfa.balance_sheet_table.map((r: any) => [r.metric, safe(r.value)]),
            [contentW * 0.6, contentW * 0.4]
          );
        }
      }

      // ================================================================
      // PROFIT & LOSS ANALYSIS (full narrative + table)
      // ================================================================
      if (cfa?.profit_loss) {
        heading('Profit & Loss Analysis');
        para(cfa.profit_loss, M, contentW, 7.5);
        y += 2;
        if (cfa.income_statement_table?.length) {
          drawTable(
            ['Metric', 'Value'],
            cfa.income_statement_table.map((r: any) => [r.metric, safe(r.value)]),
            [contentW * 0.6, contentW * 0.4]
          );
        }
      }

      // ================================================================
      // CASH FLOW ANALYSIS (full narrative + table)
      // ================================================================
      if (cfa?.cash_flow) {
        heading('Cash Flow Analysis');
        para(cfa.cash_flow, M, contentW, 7.5);
        y += 2;
        if (cfa.cash_flow_table?.length) {
          drawTable(
            ['Metric', 'Value'],
            cfa.cash_flow_table.map((r: any) => [r.metric, safe(r.value)]),
            [contentW * 0.6, contentW * 0.4]
          );
        }
      }

      // ================================================================
      // SWOT ANALYSIS (all items, no slice limits)
      // ================================================================
      if (swot && (swot.strengths?.length || swot.weaknesses?.length)) {
        heading('SWOT Analysis');
        const halfW = contentW / 2 - 2;
        const swotPairs: [string, string[], string, string, string[], string][] = [
          ['Strengths', swot.strengths || [], '#2e7d32', 'Weaknesses', swot.weaknesses || [], '#c62828'],
          ['Opportunities', swot.opportunities || [], '#1565c0', 'Threats', swot.threats || [], '#e65100'],
        ];
        for (const [lL, itemsL, cL, lR, itemsR, cR] of swotPairs) {
          newPageIfNeeded(14);
          const rowY = y;
          // Left column
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(cL);
          doc.text(lL, M, y);
          y += 3.5;
          for (const item of itemsL) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor('#333333');
            const lines: string[] = doc.splitTextToSize(`\u2022 ${item}`, halfW - 2);
            for (const line of lines) {
              newPageIfNeeded(4);
              doc.text(line, M + 1, y);
              y += 3;
            }
          }
          const leftEnd = y;
          // Right column
          y = rowY;
          const rX = M + halfW + 4;
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(cR);
          doc.text(lR, rX, y);
          y += 3.5;
          for (const item of itemsR) {
            doc.setFontSize(7);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor('#333333');
            const lines: string[] = doc.splitTextToSize(`\u2022 ${item}`, halfW - 2);
            for (const line of lines) {
              doc.text(line, rX + 1, y);
              y += 3;
            }
          }
          y = Math.max(leftEnd, y) + 2;
        }
      }

      // ================================================================
      // PEER COMPARISON (table + analysis)
      // ================================================================
      if (peer?.peers?.length) {
        heading('Peer Comparison');
        const peerCols = ['Company', 'P/E', 'EV/EBITDA', 'P/Book', 'Sales/Book'];
        const colW5 = [contentW * 0.36, contentW * 0.16, contentW * 0.16, contentW * 0.16, contentW * 0.16];
        // Current company row (highlighted)
        const companyRow = [
          `${research.company_name || research.symbol} *`,
          val.pe_ratio != null ? Number(val.pe_ratio).toFixed(1) : 'N/A',
          val.ev_to_ebitda != null ? Number(val.ev_to_ebitda).toFixed(1) : 'N/A',
          val.price_to_book != null ? Number(val.price_to_book).toFixed(1) : 'N/A',
          val.price_to_sales != null ? Number(val.price_to_sales).toFixed(1) : 'N/A',
        ];
        const peerRows = [
          companyRow,
          ...peer.peers.map((p: any) => [
            p.name || '',
            p.pe_ratio != null ? Number(p.pe_ratio).toFixed(1) : 'N/A',
            p.ev_ebitda != null ? Number(p.ev_ebitda).toFixed(1) : 'N/A',
            p.price_book != null ? Number(p.price_book).toFixed(1) : 'N/A',
            p.sales_book != null ? Number(p.sales_book).toFixed(1) : 'N/A',
          ]),
        ];
        // Draw table header
        const numCols = peerCols.length;
        newPageIfNeeded(8);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor('#1a237e');
        let pcx = M;
        for (let i = 0; i < numCols; i++) {
          const textX = i === 0 ? pcx : pcx + colW5[i];
          doc.text(peerCols[i], textX, y, { align: i === 0 ? 'left' : 'right', maxWidth: colW5[i] - 2 });
          pcx += colW5[i];
        }
        y += 1;
        doc.setDrawColor('#cccccc');
        doc.setLineWidth(0.3);
        doc.line(M, y, W - M, y);
        y += 3;
        // Draw rows — first row gets highlight
        for (let ri = 0; ri < peerRows.length; ri++) {
          const row = peerRows[ri];
          newPageIfNeeded(5);
          if (ri === 0) {
            doc.setFillColor(230, 235, 250);
            doc.rect(M, y - 2.5, contentW, 4.5, 'F');
            doc.setFont('helvetica', 'bold');
          } else {
            doc.setFont('helvetica', 'normal');
          }
          pcx = M;
          doc.setFontSize(6.5);
          doc.setTextColor('#333333');
          for (let i = 0; i < numCols; i++) {
            const textX = i === 0 ? pcx : pcx + colW5[i];
            doc.text(row[i] || '', textX, y, { align: i === 0 ? 'left' : 'right', maxWidth: colW5[i] - 2 });
            pcx += colW5[i];
          }
          y += 3.5;
        }
        y += 1;
        if (peer.analysis) {
          para(peer.analysis, M, contentW, 7.5);
          y += 2;
        }
      }

      // ================================================================
      // DCF VALUATION MODEL (full)
      // ================================================================
      if (dcf && (dcf.fair_value_per_share != null || dcf.wacc != null || dcf.narrative)) {
        heading('DCF Valuation Model');
        const curP = dcf.current_price || research.current_price;
        const fvps = dcf.fair_value_per_share;
        const implied = (fvps != null && curP) ? ((fvps - curP) / curP * 100) : null;

        // Fair Value + WACC + Terminal Growth summary
        if (fvps != null) {
          newPageIfNeeded(14);
          metricGrid([
            ['Fair Value / Share', `${tSym}${fvps.toFixed(2)}`],
            ['Current Price', curP ? `${tSym}${curP.toFixed(2)}` : 'N/A'],
            ['Implied', implied != null ? `${implied >= 0 ? '+' : ''}${implied.toFixed(1)}%` : 'N/A'],
          ], 3);
        }

        // WACC breakdown
        if (dcf.wacc_components) {
          subheading('WACC Breakdown');
          const wc = dcf.wacc_components as any;
          metricGrid([
            ['Risk-Free Rate', wc.risk_free_rate != null ? `${wc.risk_free_rate}%` : 'N/A'],
            ['Equity Risk Premium', wc.equity_risk_premium != null ? `${wc.equity_risk_premium}%` : 'N/A'],
            ['Beta', wc.beta != null ? String(wc.beta) : 'N/A'],
            ['Cost of Equity', wc.cost_of_equity != null ? `${wc.cost_of_equity}%` : 'N/A'],
            ['Cost of Debt', wc.cost_of_debt != null ? `${wc.cost_of_debt}%` : 'N/A'],
            ['WACC', dcf.wacc != null ? `${dcf.wacc}%` : 'N/A'],
          ], 3);
        }

        // Projected FCF table
        if (dcf.projected_fcf?.length) {
          subheading('Projected Free Cash Flow');
          drawTable(
            ['Year', 'FCF', 'Growth Rate'],
            dcf.projected_fcf.map((r: any) => [String(r.year), safe(r.fcf), safe(r.growth_rate)]),
            [contentW * 0.3, contentW * 0.35, contentW * 0.35]
          );
        }

        // Valuation bridge
        const bridgeItems = [
          ['Terminal Value', dcf.terminal_value],
          ['Enterprise Value', dcf.enterprise_value],
          ['Net Debt', dcf.net_debt],
          ['Equity Value', dcf.equity_value],
          ['Shares Outstanding', dcf.shares_outstanding],
        ].filter(([_, v]) => v != null) as [string, any][];
        if (bridgeItems.length) {
          subheading('Valuation Bridge');
          metricGrid(bridgeItems.map(([l, v]) => [l, String(v)]), 3);
        }

        // Sensitivity table
        if (dcf.sensitivity_table?.length) {
          subheading('Sensitivity Analysis');
          drawTable(
            ['WACC', 'Terminal Growth', 'Fair Value'],
            dcf.sensitivity_table.map((r: any) => [
              `${r.wacc}%`,
              `${r.terminal_growth}%`,
              `${tSym}${typeof r.fair_value === 'number' ? r.fair_value.toFixed(2) : r.fair_value}`,
            ]),
            [contentW * 0.3, contentW * 0.35, contentW * 0.35]
          );
        }

        // DCF narrative
        if (dcf.narrative) {
          subheading('DCF Analysis Discussion');
          para(dcf.narrative, M, contentW, 7.5);
          y += 2;
        }
      }

      // ================================================================
      // FUNDAMENTALS (boxed subsections)
      // ================================================================
      // Helper: draw a boxed metric section with title + 2-col key/value pairs
      const drawMetricBox = (title: string, pairs: [string, string][], color: string) => {
        const rowH = 4.2;
        const rows = Math.ceil(pairs.length / 2);
        const boxH = 6 + rows * rowH + 3; // title + rows + padding
        newPageIfNeeded(boxH + 4);
        // Container
        doc.setFillColor(250, 250, 254);
        doc.setDrawColor(color);
        doc.setLineWidth(0.4);
        doc.roundedRect(M, y, contentW, boxH, 1.5, 1.5, 'FD');
        y += 4.5;
        // Title
        doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(color);
        doc.text(title, M + 3, y);
        y += 4;
        // Metric pairs (2 per row)
        const halfW = (contentW - 8) / 2;
        for (let i = 0; i < pairs.length; i += 2) {
          // Left pair
          doc.setFontSize(6.8); doc.setFont('helvetica', 'normal'); doc.setTextColor('#666666');
          doc.text(pairs[i][0], M + 3, y);
          doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor('#1a1a1a');
          doc.text(pairs[i][1], M + 3 + halfW - 2, y, { align: 'right' });
          // Right pair (if exists)
          if (i + 1 < pairs.length) {
            doc.setFontSize(6.8); doc.setFont('helvetica', 'normal'); doc.setTextColor('#666666');
            doc.text(pairs[i + 1][0], M + 3 + halfW + 4, y);
            doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor('#1a1a1a');
            doc.text(pairs[i + 1][1], M + contentW - 3, y, { align: 'right' });
          }
          y += rowH;
        }
        y += 3;
      };

      if (fund.has_data) {
        heading('Fundamentals');

        if (val && Object.keys(val).length > 1) {
          drawMetricBox('Valuation', [
            ['Market Cap', fmtBig(val.market_cap)],
            ['P/E (TTM)', f2(val.pe_ratio)],
            ['Forward P/E', f2(val.forward_pe)],
            ['PEG Ratio', f2(val.peg_ratio)],
            ['Price/Book', f2(val.price_to_book)],
            ['Price/Sales', f2(val.price_to_sales)],
            ['EV/Revenue', f2(val.ev_to_revenue)],
            ['EV/EBITDA', f2(val.ev_to_ebitda)],
          ], '#1a237e');
        }

        if (prof && (prof.profit_margin != null || prof.operating_margin != null)) {
          drawMetricBox('Profitability', [
            ['Gross Margin', pct(prof.gross_margin)],
            ['Operating Margin', pct(prof.operating_margin)],
            ['Profit Margin', pct(prof.profit_margin)],
            ['ROE', pct(prof.roe)],
            ['ROA', pct(prof.roa)],
          ], '#2e7d32');
        }

        if (gr && (gr.revenue_growth != null || gr.earnings_growth != null)) {
          drawMetricBox('Growth', [
            ['Revenue Growth', pct(gr.revenue_growth)],
            ['Earnings Growth', pct(gr.earnings_growth)],
            ['EPS (TTM)', gr.eps_ttm != null ? `${tSym}${gr.eps_ttm.toFixed(2)}` : 'N/A'],
            ['EPS (Forward)', gr.eps_forward != null ? `${tSym}${gr.eps_forward.toFixed(2)}` : 'N/A'],
          ], '#e65100');
        }

        if (fh && (fh.total_cash != null || fh.total_debt != null)) {
          drawMetricBox('Financial Health', [
            ['Total Cash', fmtBig(fh.total_cash)],
            ['Total Debt', fmtBig(fh.total_debt)],
            ['Free Cash Flow', fmtBig(fh.free_cash_flow)],
            ['D/E Ratio', fh.debt_to_equity != null ? fh.debt_to_equity.toFixed(2) : 'N/A'],
            ['Current Ratio', fh.current_ratio != null ? fh.current_ratio.toFixed(2) : 'N/A'],
          ], '#1565c0');
        }
      }

      // ================================================================
      // FINANCIAL STATEMENTS (income, balance, cash flow tables)
      // ================================================================
      if (research.financial_statements) {
        const stmts: [string, string][] = [
          ['Income Statement', 'income_statement'],
          ['Balance Sheet', 'balance_sheet'],
          ['Cash Flow Statement', 'cash_flow'],
        ];
        for (const [title, key] of stmts) {
          const annual = (research.financial_statements as any)?.[key]?.annual;
          if (!annual || !annual.periods?.length || !annual.rows?.length) continue;

          heading(title);
          // Build table
          const periods = annual.periods as string[];
          const headers = ['Metric', ...periods];
          const colW_stmt = [contentW * 0.36, ...periods.map(() => (contentW * 0.64) / periods.length)];
          const rows = (annual.rows as any[]).map((row: any) => [
            row.metric,
            ...row.values.map((v: any) => v ?? '\u2014'),
          ]);
          drawTable(headers, rows, colW_stmt);
        }
      }

      // ================================================================
      // TECHNICAL ANALYSIS (chart + performance + indicators + events)
      // ================================================================
      const perf = research.performance;
      const tsum = research.technical_summary;
      const tevts = research.technical_events;

      if (perf || tsum || tevts?.length || chartImgData) {
        heading('Technical Analysis');

        // Price chart image
        if (chartImgData) {
          newPageIfNeeded(75);
          const imgW = contentW;
          const imgH = imgW * 0.42;
          try {
            doc.addImage(chartImgData, 'PNG', M, y, imgW, imgH);
            y += imgH + 4;
          } catch (imgErr) {
            console.warn('Failed to add chart image to PDF:', imgErr);
          }
        }

        // Performance box
        if (perf) {
          const perfPairs: [string, string][] = [];
          if (perf.week_1 != null) perfPairs.push(['1 Week', `${perf.week_1 >= 0 ? '+' : ''}${perf.week_1.toFixed(1)}%`]);
          if (perf.month_1 != null) perfPairs.push(['1 Month', `${perf.month_1 >= 0 ? '+' : ''}${perf.month_1.toFixed(1)}%`]);
          if (perf.month_3 != null) perfPairs.push(['3 Months', `${perf.month_3 >= 0 ? '+' : ''}${perf.month_3.toFixed(1)}%`]);
          if (perf.ytd != null) perfPairs.push(['YTD', `${perf.ytd >= 0 ? '+' : ''}${perf.ytd.toFixed(1)}%`]);
          if (perf.year_1 != null) perfPairs.push(['1 Year', `${perf.year_1 >= 0 ? '+' : ''}${perf.year_1.toFixed(1)}%`]);
          if (perfPairs.length) drawMetricBox('Performance', perfPairs, '#1a237e');
        }

        // Technical indicators box
        if (tsum) {
          const indPairs: [string, string][] = [];
          if (tsum.rsi != null) indPairs.push(['RSI (14)', `${tsum.rsi} (${tsum.rsi_label})`]);
          if (tsum.momentum != null) indPairs.push(['Momentum (10d)', `${tsum.momentum >= 0 ? '+' : ''}${tsum.momentum.toFixed(1)}%`]);
          if (tsum.volatility != null) indPairs.push(['Historical Vol (20d)', `${tsum.volatility.toFixed(1)}% (${tsum.volatility_label})`]);
          if (tsum.implied_volatility != null) indPairs.push(['Implied Vol (30d)', `${tsum.implied_volatility.toFixed(1)}% (${tsum.iv_label})`]);
          if (tsum.price_vs_ma20 != null) indPairs.push(['vs 20 MA', `${tsum.price_vs_ma20 >= 0 ? '+' : ''}${tsum.price_vs_ma20.toFixed(1)}%`]);
          if (tsum.price_vs_ma50 != null) indPairs.push(['vs 50 MA', `${tsum.price_vs_ma50 >= 0 ? '+' : ''}${tsum.price_vs_ma50.toFixed(1)}%`]);
          if (tsum.price_vs_ma200 != null) indPairs.push(['vs 200 MA', `${tsum.price_vs_ma200 >= 0 ? '+' : ''}${tsum.price_vs_ma200.toFixed(1)}%`]);
          if (indPairs.length) drawMetricBox('Technical Indicators', indPairs, '#6a1b9a');
        }

        // Technical events
        if (tevts?.length) {
          subheading('Recent Technical Events');
          for (const evt of tevts.slice(0, 8)) {
            newPageIfNeeded(5);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(evt.sentiment === 'bullish' ? '#2e7d32' : '#c62828');
            doc.text(evt.sentiment === 'bullish' ? 'BULL' : 'BEAR', M, y);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor('#666666');
            doc.text(evt.date, M + 12, y);
            doc.setTextColor('#333333');
            const evtText = `${evt.title} — ${evt.description}`;
            const evtLines: string[] = doc.splitTextToSize(evtText, contentW - 34);
            for (const line of evtLines) {
              doc.text(line, M + 32, y);
              y += 3.5;
            }
          }
          y += 1;
        }
      }

      // ================================================================
      // RECENT NEWS
      // ================================================================
      if (research.recent_news?.length) {
        heading('Recent News');
        for (const news of research.recent_news) {
          newPageIfNeeded(8);
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor('#333333');
          const titleLines: string[] = doc.splitTextToSize(`\u2022 ${news.title}`, contentW - 2);
          for (const line of titleLines) {
            newPageIfNeeded(4);
            doc.text(line, M + 1, y);
            y += 3;
          }
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor('#888888');
          const meta = [news.source, (news as any).date || news.published_at].filter(Boolean).join(' \u2022 ');
          if (meta) { doc.text(meta, M + 4, y); y += 2.5; }
          y += 1;
        }
      }

      // ================================================================
      // UPCOMING EVENTS
      // ================================================================
      if (research.upcoming_events?.length) {
        heading('Upcoming Events');
        for (const evt of research.upcoming_events) {
          newPageIfNeeded(5);
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor('#333333');
          doc.text(`\u2022 ${evt.date}: ${evt.event}`, M + 1, y);
          y += 4;
        }
      }

      // ================================================================
      // RECOMMENDATION BOX
      // ================================================================
      if (research.recommendation) {
        const rating = research.recommendation.rating;
        const sumText = research.recommendation.summary || (research.recommendation as any).rationale || '';
        doc.setFontSize(7.5);
        const sumLines: string[] = doc.splitTextToSize(sumText, contentW - 10);
        const boxH = Math.max(14, 10 + sumLines.length * 3.2);
        newPageIfNeeded(boxH + 6);
        y += 3;

        const bg = rating === 'BUY' ? '#e8f5e9' : rating === 'SELL' ? '#ffebee' : '#fff8e1';
        const border = rating === 'BUY' ? '#2e7d32' : rating === 'SELL' ? '#c62828' : '#e65100';
        doc.setFillColor(bg);
        doc.setDrawColor(border);
        doc.setLineWidth(0.5);
        doc.roundedRect(M, y, contentW, boxH, 2, 2, 'FD');
        y += 5.5;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(border);
        doc.text(`Recommendation: ${rating}`, M + 5, y);
        y += 4.5;
        if (sumText) {
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor('#333333');
          for (const line of sumLines) {
            if (y > (y + boxH - 4)) break;
            doc.text(line, M + 5, y);
            y += 3.2;
          }
        }
        y += 2;
      }

      // ================================================================
      // FOOTER on every page
      // ================================================================
      const totalPages = doc.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor('#dddddd');
        doc.setLineWidth(0.3);
        doc.line(M, H - 10, W - M, H - 10);
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor('#aaaaaa');
        doc.text('AI-generated research for informational purposes only. Not financial advice.', M, H - 6);
        doc.text(`${research.symbol} Investment Memo  |  Page ${i} of ${totalPages}`, W - M, H - 6, { align: 'right' });
      }

      const ts = new Date().toISOString().split('T')[0];
      doc.save(`${research.symbol}_Investment_Memo_${ts}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        AI Research Assistant
      </Typography>

      {/* Search Bar */}
      <Paper sx={{ p: 3, mb: 3 }} elevation={3}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Stock Research & Analysis
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Enter a ticker symbol to generate comprehensive AI-powered research and insights. Supports international tickers (e.g., PSN.L for London Stock Exchange).
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Autocomplete
            fullWidth
            freeSolo
            options={searchOptions}
            getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.symbol}
            filterOptions={(x) => x}
            loading={searchLoading}
            inputValue={ticker}
            onInputChange={(_e, value, reason) => {
              const upper = value.toUpperCase();
              setTicker(upper);
              if (reason === 'reset') return;
              if (searchDebounce.current) clearTimeout(searchDebounce.current);
              if (upper.length < 1) { setSearchOptions([]); return; }
              searchDebounce.current = setTimeout(async () => {
                setSearchLoading(true);
                try {
                  const res = await fetch(`${API_BASE_URL}/api/correlation/search?q=${encodeURIComponent(upper)}&limit=8`);
                  const data = await res.json();
                  setSearchOptions(data.results || []);
                } catch { setSearchOptions([]); }
                finally { setSearchLoading(false); }
              }, 200);
            }}
            onChange={(_e, value) => {
              if (value && typeof value !== 'string') {
                setTicker(value.symbol);
              }
            }}
            renderOption={(props, opt) => (
              <Box component="li" {...props} key={opt.symbol} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: '6px !important' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.accent.orange }}>{opt.symbol}</Typography>
                  {opt.country && <Typography sx={{ fontSize: '0.7rem', color: bloomberg.text.muted }}>{opt.country}</Typography>}
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: bloomberg.text.secondary }}>{opt.name}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by ticker or company name (e.g., Apple, TSLA, PSN.L)"
                onKeyPress={handleKeyPress}
                disabled={loading}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': { fontSize: '1.1rem', fontWeight: 500 }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ minWidth: 140, height: 56 }}
          >
            Research
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Saved Reports */}
      {savedReports.length > 0 && !research && !loading && (() => {
        const uniqueSectors = Array.from(new Set(savedReports.map(r => r.sector).filter(Boolean))) as string[];
        const filteredReports = savedReports.filter(r => {
          const q = reportSearch.toLowerCase();
          const matchSearch = !q || r.symbol.toLowerCase().includes(q) || (r.company_name || '').toLowerCase().includes(q);
          const matchSector = !reportSectorFilter || r.sector === reportSectorFilter;
          const matchRating = !reportRatingFilter || r.recommendation === reportRatingFilter;
          return matchSearch && matchSector && matchRating;
        });
        return (
        <Paper sx={{ p: 3, mb: 3 }} elevation={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Saved Reports</Typography>
            <Typography variant="body2" sx={{ color: bloomberg.text.secondary, ml: 1 }}>
              {filteredReports.length} of {savedReports.length}
            </Typography>
          </Box>
          {/* Filter bar */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search ticker or name…"
              value={reportSearch}
              onChange={e => setReportSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 200, flex: 1 }}
            />
            <Select
              size="small"
              displayEmpty
              value={reportSectorFilter}
              onChange={e => setReportSectorFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value=""><em>All Sectors</em></MenuItem>
              {uniqueSectors.sort().map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
            <Select
              size="small"
              displayEmpty
              value={reportRatingFilter}
              onChange={e => setReportRatingFilter(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value=""><em>All Ratings</em></MenuItem>
              {['BUY', 'HOLD', 'SELL'].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
            {(reportSearch || reportSectorFilter || reportRatingFilter) && (
              <Button size="small" onClick={() => { setReportSearch(''); setReportSectorFilter(''); setReportRatingFilter(''); }}>
                Clear
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Symbol</strong></TableCell>
                  <TableCell><strong>Company</strong></TableCell>
                  <TableCell><strong>Sector / Industry</strong></TableCell>
                  <TableCell align="right"><strong>Price</strong></TableCell>
                  <TableCell align="center"><strong>Rating</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReports.map((report) => (
                  <TableRow
                    key={report.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleLoadSaved(report.id)}
                  >
                    <TableCell>
                      <Typography fontWeight={600}>{report.symbol}</Typography>
                    </TableCell>
                    <TableCell>{report.company_name || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {report.sector && (
                          <Typography variant="caption" sx={{ color: bloomberg.text.primary, fontWeight: 500 }}>
                            {report.sector}
                          </Typography>
                        )}
                        {report.industry && (
                          <Typography variant="caption" sx={{ color: bloomberg.text.secondary }}>
                            {report.industry}
                          </Typography>
                        )}
                        {!report.sector && !report.industry && '-'}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {report.current_price ? `${report.trading_symbol || '$'}${report.current_price.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {report.recommendation && (
                        <Chip
                          label={report.recommendation}
                          size="small"
                          color={
                            report.recommendation === 'BUY' ? 'success' :
                            report.recommendation === 'SELL' ? 'error' : 'default'
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {report.created_at ? new Date(report.created_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteReport(report.id, e)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredReports.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: bloomberg.text.secondary }}>
                      No reports match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        );
      })()}

      {/* Loading State */}
      {loading && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Generating comprehensive AI-powered research report... This may take 30-60 seconds.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Please don't close this page. Gathering real-time market data and generating CFA-level analysis with DCF valuation.
            </Typography>
          </Alert>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
            <Box>
              <Paper sx={{ p: 3, mb: 3 }} elevation={3}>
                <Skeleton variant="text" width="60%" height={40} />
                <Skeleton variant="text" width="40%" sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="text" width="80%" />
              </Paper>
            </Box>
            <Box>
              <Paper sx={{ p: 3 }} elevation={3}>
                <Skeleton variant="text" width="60%" height={30} />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Paper>
            </Box>
          </Box>
        </Box>
      )}

      {/* Research Results */}
      {!loading && research && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Header Section */}
          <Paper sx={{ p: 1.5, mb: 1 }} elevation={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => setResearch(null)} size="small" sx={{ color: 'text.secondary' }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {research.symbol}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {research.company_name}
                    </Typography>
                    {research.sector && (
                      <Chip label={research.sector} size="small" sx={{ fontWeight: 600, height: 20, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {(research as any).trading_symbol || '$'}{research.current_price?.toFixed(2) || 'N/A'}
                  </Typography>
                </Box>
                {research.price_change !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {research.price_change >= 0 ? (
                      <TrendingUpIcon sx={{ mr: 0.25, fontSize: 18, color: 'success.main' }} />
                    ) : (
                      <TrendingDownIcon sx={{ mr: 0.25, fontSize: 18, color: 'error.main' }} />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 600, color: research.price_change >= 0 ? 'success.main' : 'error.main' }}>
                      {research.price_change >= 0 ? '+' : ''}
                      {research.price_change?.toFixed(2)}%
                    </Typography>
                  </Box>
                )}
                <MuiTooltip title="Add to Watchlist">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const current = getWatchlist();
                      if (!current.includes(research.symbol)) {
                        saveWatchlist([...current, research.symbol]);
                        setActionSnackbar(`${research.symbol} added to watchlist`);
                      } else {
                        setActionSnackbar(`${research.symbol} already in watchlist`);
                      }
                    }}
                    sx={{ color: bloomberg.accent.orange }}
                  >
                    <BookmarkAddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </MuiTooltip>
                <MuiTooltip title="Add to Portfolio">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const current = getTickers();
                      if (!current.includes(research.symbol)) {
                        saveTickers([...current, research.symbol]);
                        setActionSnackbar(`${research.symbol} added to portfolio`);
                      } else {
                        setActionSnackbar(`${research.symbol} already in portfolio`);
                      }
                    }}
                    sx={{ color: '#22c55e' }}
                  >
                    <PlaylistAddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </MuiTooltip>
                <MuiTooltip title="View Chart">
                  <IconButton
                    size="small"
                    onClick={() => navigateTo(`/analytics?symbol=${research.symbol}`)}
                    sx={{ color: '#22d3ee' }}
                  >
                    <ShowChartIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </MuiTooltip>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />}
                  onClick={downloadReport}
                  sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.5 }}
                >
                  PDF
                </Button>
              </Box>
            </Box>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, overflow: 'hidden' }}>
            {/* Main Content */}
            <Box sx={{ pr: 0.5, minWidth: 0 }}>
              {/* AI Executive Summary */}
              <Card sx={{ mb: 1.5 }} elevation={3}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssessmentIcon sx={{ mr: 1, color: '#667eea', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Executive Summary
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.primary', whiteSpace: 'pre-line' }}>
                    {research.ai_summary}
                  </Typography>
                </CardContent>
              </Card>

              {/* Key Insights */}
              {research.key_insights && research.key_insights.length > 0 && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LightbulbIcon sx={{ mr: 1, color: '#f59e0b', fontSize: 28 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Key Insights
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {research.key_insights.map((insight, idx) => (
                        <Box 
                          key={idx}
                          sx={{ 
                            p: 2, 
                            bgcolor: 'background.default', 
                            borderRadius: 2,
                            borderLeft: '4px solid #f59e0b'
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {insight}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Analysis Sections */}
              {/* CFA-Level Financial Analysis */}
              {research.cfa_analysis && (
                <>
                  {/* Balance Sheet Analysis */}
                  {research.cfa_analysis.balance_sheet && (
                    <Card sx={{ mb: 1.5 }} elevation={3}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Balance Sheet Analysis
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line', mb: 3 }}>
                          {research.cfa_analysis.balance_sheet}
                        </Typography>
                        
                        {/* Balance Sheet Table */}
                        {research.cfa_analysis.balance_sheet_table && research.cfa_analysis.balance_sheet_table.length > 0 && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                              Balance Sheet (Key Metrics)
                            </Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.2)' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Metric</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {research.cfa_analysis.balance_sheet_table.map((item, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                      <td style={{ padding: '12px' }}>{item.metric}</td>
                                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 500 }}>
                                        {item.value}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* P&L Analysis */}
                  {research.cfa_analysis.profit_loss && (
                    <Card sx={{ mb: 1.5 }} elevation={3}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <ShowChartIcon sx={{ mr: 1, color: 'success.main' }} />
                          <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Profit & Loss Analysis
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line', mb: 3 }}>
                          {research.cfa_analysis.profit_loss}
                        </Typography>
                        
                        {/* Income Statement Table */}
                        {research.cfa_analysis.income_statement_table && research.cfa_analysis.income_statement_table.length > 0 && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                              Income Statement (Key Metrics)
                            </Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.2)' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Metric</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {research.cfa_analysis.income_statement_table.map((item, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                      <td style={{ padding: '12px' }}>{item.metric}</td>
                                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 500 }}>
                                        {item.value}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Cash Flow Analysis */}
                  {research.cfa_analysis.cash_flow && (
                    <Card sx={{ mb: 1.5 }} elevation={3}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <TrendingUpIcon sx={{ mr: 1, color: 'info.main' }} />
                          <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Cash Flow Analysis
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line', mb: 3 }}>
                          {research.cfa_analysis.cash_flow}
                        </Typography>
                        
                        {/* Cash Flow Statement Table */}
                        {research.cfa_analysis.cash_flow_table && research.cfa_analysis.cash_flow_table.length > 0 && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                              Cash Flow Statement (Key Metrics)
                            </Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.2)' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Metric</th>
                                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Value</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {research.cfa_analysis.cash_flow_table.map((item, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                      <td style={{ padding: '12px' }}>{item.metric}</td>
                                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 500 }}>
                                        {item.value}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Analyst Consensus */}
                  {research.cfa_analysis.analyst_consensus && (
                    <Card sx={{ mb: 1.5 }} elevation={3}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <BarChartIcon sx={{ mr: 1, color: 'warning.main' }} />
                          <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            Analyst Consensus (Past 12 Months)
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                          {research.cfa_analysis.analyst_consensus}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}

                  {/* SWOT Analysis */}
                  {research.cfa_analysis.swot && (
                    <Card sx={{ mb: 1.5 }} elevation={3}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <LightbulbIcon sx={{ mr: 1, color: 'secondary.main' }} />
                          <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            SWOT Analysis
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                          {/* Strengths */}
                          {research.cfa_analysis.swot.strengths && research.cfa_analysis.swot.strengths.length > 0 && (
                            <Box>
                              <Typography variant="h6" sx={{ mb: 1, color: 'success.main', fontWeight: 600 }}>
                                Strengths
                              </Typography>
                              <Box component="ul" sx={{ pl: 2 }}>
                                {research.cfa_analysis.swot.strengths.map((strength, idx) => (
                                  <Typography component="li" key={idx} variant="body2" sx={{ mb: 1 }}>
                                    {strength}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Weaknesses */}
                          {research.cfa_analysis.swot.weaknesses && research.cfa_analysis.swot.weaknesses.length > 0 && (
                            <Box>
                              <Typography variant="h6" sx={{ mb: 1, color: 'error.main', fontWeight: 600 }}>
                                Weaknesses
                              </Typography>
                              <Box component="ul" sx={{ pl: 2 }}>
                                {research.cfa_analysis.swot.weaknesses.map((weakness, idx) => (
                                  <Typography component="li" key={idx} variant="body2" sx={{ mb: 1 }}>
                                    {weakness}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Opportunities */}
                          {research.cfa_analysis.swot.opportunities && research.cfa_analysis.swot.opportunities.length > 0 && (
                            <Box>
                              <Typography variant="h6" sx={{ mb: 1, color: 'info.main', fontWeight: 600 }}>
                                Opportunities
                              </Typography>
                              <Box component="ul" sx={{ pl: 2 }}>
                                {research.cfa_analysis.swot.opportunities.map((opportunity, idx) => (
                                  <Typography component="li" key={idx} variant="body2" sx={{ mb: 1 }}>
                                    {opportunity}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Threats */}
                          {research.cfa_analysis.swot.threats && research.cfa_analysis.swot.threats.length > 0 && (
                            <Box>
                              <Typography variant="h6" sx={{ mb: 1, color: 'warning.main', fontWeight: 600 }}>
                                Threats
                              </Typography>
                              <Box component="ul" sx={{ pl: 2 }}>
                                {research.cfa_analysis.swot.threats.map((threat, idx) => (
                                  <Typography component="li" key={idx} variant="body2" sx={{ mb: 1 }}>
                                    {threat}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Comparable Company Analysis */}
                  {(() => {
                    const compCellSx = { fontSize: '0.7rem', py: 0.5, px: 0.75, whiteSpace: 'nowrap' } as const;
                    const compHeaderSx = { fontSize: '0.7rem', fontWeight: 700, py: 0.5, px: 0.75, bgcolor: bloomberg.bg.tertiary, color: bloomberg.accent.orange, whiteSpace: 'nowrap' } as const;
                    const fmtNum = (v: any, d: number = 1) => v != null && typeof v === 'number' ? v.toFixed(d) : '-';
                    const fmtPct = (v: any) => v != null && typeof v === 'number' ? `${v > 0 ? '+' : ''}${v.toFixed(1)}%` : '-';
                    const fmtMcap = (v: any) => {
                      if (!v) return '-';
                      if (v >= 1e12) return `${(v / 1e12).toFixed(1)}T`;
                      if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
                      if (v >= 1e6) return `${(v / 1e6).toFixed(0)}M`;
                      return v.toLocaleString();
                    };
                    const pctColor = (v: any) => v != null ? (v > 0 ? '#22c55e' : v < 0 ? '#ef4444' : 'inherit') : 'text.secondary';

                    const hasComps = comps && comps.target && comps.peers && comps.peers.length > 0;
                    const hasAiPeers = research.cfa_analysis?.peer_comparison?.peers?.length > 0;

                    if (!hasComps && !hasAiPeers && !compsLoading) return null;

                    return (
                      <Card sx={{ mb: 1.5 }} elevation={3}>
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <BarChartIcon sx={{ mr: 1, color: bloomberg.accent.orange }} />
                            <Typography variant="h5" sx={{ fontWeight: 600 }}>
                              Comparable Company Analysis
                            </Typography>
                            {comps?.industry && (
                              <Chip label={comps.industry} size="small" sx={{ ml: 2, fontSize: '0.7rem', height: 22 }} variant="outlined" />
                            )}
                          </Box>

                          {compsLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                              <CircularProgress size={24} />
                              <Typography sx={{ ml: 1, fontSize: '0.8rem', color: 'text.secondary' }}>Loading comparable companies...</Typography>
                            </Box>
                          )}

                          {hasComps && (() => {
                            const allRows = [comps.target, ...comps.peers];
                            const targetSym = comps.target.symbol;

                            return (
                              <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: 'rgba(255,255,255,0.06)' } }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={compHeaderSx}>Company</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">Mkt Cap</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">P/E</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">Fwd P/E</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">EV/EBITDA</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">P/B</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">P/S</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">Yield</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">Beta</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">ROE</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">Margin</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">D/E</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">1W</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">1M</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">3M</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">YTD</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">1Y</TableCell>
                                      <TableCell sx={{ ...compHeaderSx, borderLeft: '1px solid rgba(255,255,255,0.08)' }} align="right">RSI</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">vs 20MA</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">vs 50MA</TableCell>
                                      <TableCell sx={compHeaderSx} align="right">vs 200MA</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {allRows.map((row: any, idx: number) => {
                                      const isTarget = row.symbol === targetSym;
                                      return (
                                        <TableRow
                                          key={row.symbol}
                                          sx={{
                                            bgcolor: isTarget ? bloomberg.accent.orangeDim : 'inherit',
                                            borderLeft: isTarget ? `3px solid ${bloomberg.accent.orange}` : 'none',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                                          }}
                                        >
                                          <TableCell sx={{ ...compCellSx, fontWeight: isTarget ? 700 : 400, minWidth: 140 }}>
                                            <Box>
                                              <Typography sx={{ fontSize: '0.7rem', fontWeight: isTarget ? 700 : 500 }}>
                                                {row.symbol}{isTarget ? ' *' : ''}
                                              </Typography>
                                              <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }} noWrap>
                                                {row.name}
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell sx={compCellSx} align="right">{fmtMcap(row.market_cap)}</TableCell>
                                          <TableCell sx={compCellSx} align="right">
                                            {fmtNum(row.pe_ratio)}

                                          </TableCell>
                                          <TableCell sx={compCellSx} align="right">
                                            {fmtNum(row.forward_pe)}

                                          </TableCell>
                                          <TableCell sx={compCellSx} align="right">
                                            {fmtNum(row.ev_ebitda)}

                                          </TableCell>
                                          <TableCell sx={compCellSx} align="right">
                                            {fmtNum(row.price_to_book)}

                                          </TableCell>
                                          <TableCell sx={compCellSx} align="right">
                                            {fmtNum(row.price_to_sales)}

                                          </TableCell>
                                          <TableCell sx={compCellSx} align="right">
                                            {row.dividend_yield != null ? `${fmtNum(row.dividend_yield)}%` : '-'}
                                          </TableCell>
                                          <TableCell sx={compCellSx} align="right">{fmtNum(row.beta, 2)}</TableCell>
                                          <TableCell sx={compCellSx} align="right">
                                            {row.roe != null ? `${fmtNum(row.roe)}%` : '-'}

                                          </TableCell>
                                          <TableCell sx={compCellSx} align="right">
                                            {row.profit_margin != null ? `${fmtNum(row.profit_margin)}%` : '-'}
                                          </TableCell>
                                          <TableCell sx={compCellSx} align="right">{fmtNum(row.debt_to_equity)}</TableCell>
                                          <TableCell sx={{ ...compCellSx, color: pctColor(row.week_1) }} align="right">{fmtPct(row.week_1)}</TableCell>
                                          <TableCell sx={{ ...compCellSx, color: pctColor(row.month_1) }} align="right">{fmtPct(row.month_1)}</TableCell>
                                          <TableCell sx={{ ...compCellSx, color: pctColor(row.month_3) }} align="right">{fmtPct(row.month_3)}</TableCell>
                                          <TableCell sx={{ ...compCellSx, color: pctColor(row.ytd) }} align="right">{fmtPct(row.ytd)}</TableCell>
                                          <TableCell sx={{ ...compCellSx, color: pctColor(row.year_1) }} align="right">{fmtPct(row.year_1)}</TableCell>
                                          <TableCell sx={{ ...compCellSx, borderLeft: '1px solid rgba(255,255,255,0.08)', color: row.rsi != null ? (row.rsi >= 70 ? '#ef4444' : row.rsi <= 30 ? '#22c55e' : 'inherit') : 'text.secondary' }} align="right">
                                            {row.rsi != null ? row.rsi.toFixed(1) : '—'}
                                          </TableCell>
                                          <TableCell sx={{ ...compCellSx, color: pctColor(row.pct_from_sma20) }} align="right">{row.pct_from_sma20 != null ? `${row.pct_from_sma20 > 0 ? '+' : ''}${row.pct_from_sma20.toFixed(1)}%` : '—'}</TableCell>
                                          <TableCell sx={{ ...compCellSx, color: pctColor(row.pct_from_sma50) }} align="right">{row.pct_from_sma50 != null ? `${row.pct_from_sma50 > 0 ? '+' : ''}${row.pct_from_sma50.toFixed(1)}%` : '—'}</TableCell>
                                          <TableCell sx={{ ...compCellSx, color: pctColor(row.pct_from_sma200) }} align="right">{row.pct_from_sma200 != null ? `${row.pct_from_sma200 > 0 ? '+' : ''}${row.pct_from_sma200.toFixed(1)}%` : '—'}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            );
                          })()}

                          {/* Fallback: AI-generated peer comparison if no DB comps */}
                          {!hasComps && !compsLoading && hasAiPeers && (
                            <Box sx={{ overflowX: 'auto', mb: 2 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: `2px solid ${bloomberg.accent.orange}` }}>
                                    <th style={{ padding: '8px', textAlign: 'left', fontSize: '0.75rem' }}>Company</th>
                                    <th style={{ padding: '8px', textAlign: 'right', fontSize: '0.75rem' }}>P/E</th>
                                    <th style={{ padding: '8px', textAlign: 'right', fontSize: '0.75rem' }}>EV/EBITDA</th>
                                    <th style={{ padding: '8px', textAlign: 'right', fontSize: '0.75rem' }}>P/Book</th>
                                    <th style={{ padding: '8px', textAlign: 'right', fontSize: '0.75rem' }}>Sales/Book</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {research.cfa_analysis.peer_comparison.peers.map((peer: any, idx: number) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                                      <td style={{ padding: '8px', fontSize: '0.75rem' }}>{peer.name}</td>
                                      <td style={{ padding: '8px', textAlign: 'right', fontSize: '0.75rem' }}>{typeof peer.pe_ratio === 'number' ? peer.pe_ratio.toFixed(2) : 'N/A'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right', fontSize: '0.75rem' }}>{typeof peer.ev_ebitda === 'number' ? peer.ev_ebitda.toFixed(2) : 'N/A'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right', fontSize: '0.75rem' }}>{typeof peer.price_book === 'number' ? peer.price_book.toFixed(2) : 'N/A'}</td>
                                      <td style={{ padding: '8px', textAlign: 'right', fontSize: '0.75rem' }}>{typeof peer.sales_book === 'number' ? peer.sales_book.toFixed(2) : 'N/A'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          )}

                          {/* AI Analysis text (kept from research report) */}
                          {research.cfa_analysis?.peer_comparison?.analysis && (
                            <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8, whiteSpace: 'pre-line', color: 'text.secondary' }}>
                              {research.cfa_analysis.peer_comparison.analysis}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}
                </>
              )}

              {/* DCF Valuation Model */}
              {research.dcf_analysis && research.dcf_analysis.fair_value_per_share != null && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <AccountBalanceIcon sx={{ mr: 1, color: '#8b5cf6' }} />
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        DCF Valuation Model
                      </Typography>
                    </Box>

                    {/* Fair Value Summary */}
                    {(() => {
                      const dcf = research.dcf_analysis!;
                      const fairValue = dcf.fair_value_per_share;
                      const currentPrice = dcf.current_price || research.current_price;
                      const upside = fairValue && currentPrice
                        ? ((fairValue - currentPrice) / currentPrice * 100)
                        : null;
                      const isUpside = upside != null && upside >= 0;

                      return (
                        <Box sx={{
                          display: 'flex',
                          gap: 3,
                          mb: 3,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: isUpside ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                          border: 1,
                          borderColor: isUpside ? 'success.main' : 'error.main',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                        }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Fair Value</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                              {(research as any).trading_symbol || '$'}{fairValue?.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Current Price</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 500 }}>
                              {(research as any).trading_symbol || '$'}{currentPrice?.toFixed(2)}
                            </Typography>
                          </Box>
                          {upside != null && (
                            <Chip
                              label={`${upside >= 0 ? '+' : ''}${upside.toFixed(1)}% ${upside >= 0 ? 'Upside' : 'Downside'}`}
                              color={isUpside ? 'success' : 'error'}
                              sx={{ fontWeight: 700, fontSize: '1rem', height: 36, px: 1 }}
                            />
                          )}
                          {dcf.methodology && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                              {dcf.methodology}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}

                    {/* WACC Breakdown */}
                    {research.dcf_analysis.wacc_components && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                          WACC Breakdown
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {[
                            { label: 'Risk-Free Rate', value: research.dcf_analysis.wacc_components.risk_free_rate, suffix: '%' },
                            { label: 'Beta', value: research.dcf_analysis.wacc_components.beta, suffix: '' },
                            { label: 'Cost of Equity', value: research.dcf_analysis.wacc_components.cost_of_equity, suffix: '%' },
                            { label: 'Cost of Debt', value: research.dcf_analysis.wacc_components.cost_of_debt, suffix: '%' },
                            { label: 'WACC', value: research.dcf_analysis.wacc, suffix: '%' },
                          ].map((item) => (
                            <Box key={item.label} sx={{ textAlign: 'center', p: 1, border: 1, borderColor: 'divider', borderRadius: 1, minWidth: 100 }}>
                              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {item.value != null ? `${item.value}${item.suffix}` : 'N/A'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Projected FCF Table */}
                    {research.dcf_analysis.projected_fcf && research.dcf_analysis.projected_fcf.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                          Projected Free Cash Flow
                        </Typography>
                        <Box sx={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #ddd' }}>
                                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Year</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>FCF</th>
                                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Growth Rate</th>
                              </tr>
                            </thead>
                            <tbody>
                              {research.dcf_analysis.projected_fcf.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{item.year}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{item.fcf}</td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{item.growth_rate}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Box>
                    )}

                    {/* Valuation Bridge */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        Valuation Bridge
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        {[
                          { label: 'Terminal Value', value: research.dcf_analysis.terminal_value },
                          { label: 'Enterprise Value', value: research.dcf_analysis.enterprise_value },
                          { label: 'Net Debt', value: research.dcf_analysis.net_debt },
                          { label: 'Equity Value', value: research.dcf_analysis.equity_value },
                          { label: 'Shares', value: research.dcf_analysis.shares_outstanding },
                        ].filter(item => item.value).map((item, idx, arr) => (
                          <React.Fragment key={item.label}>
                            <Box sx={{ textAlign: 'center', p: 1, border: 1, borderColor: 'divider', borderRadius: 1, minWidth: 100 }}>
                              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.value}</Typography>
                            </Box>
                            {idx < arr.length - 1 && (
                              <Typography variant="h6" color="text.secondary">→</Typography>
                            )}
                          </React.Fragment>
                        ))}
                      </Box>
                    </Box>

                    {/* Sensitivity Grid */}
                    {research.dcf_analysis.sensitivity_table && research.dcf_analysis.sensitivity_table.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                          Sensitivity Analysis
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Fair value per share at different WACC and terminal growth rate assumptions
                        </Typography>
                        {(() => {
                          const table = research.dcf_analysis!.sensitivity_table!;
                          const waccs = Array.from(new Set(table.map(r => r.wacc))).sort((a, b) => a - b);
                          const growths = Array.from(new Set(table.map(r => r.terminal_growth))).sort((a, b) => a - b);
                          const baseWacc = research.dcf_analysis!.wacc;
                          const baseGrowth = research.dcf_analysis!.terminal_growth_rate;
                          const currentPrice = research.dcf_analysis!.current_price || research.current_price;

                          const lookup = new Map<string, number>();
                          table.forEach(r => lookup.set(`${r.wacc}-${r.terminal_growth}`, r.fair_value));

                          return (
                            <Box sx={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>WACC \ Growth</th>
                                    {growths.map(g => (
                                      <th key={g} style={{
                                        padding: '8px 12px',
                                        textAlign: 'right',
                                        fontWeight: g === baseGrowth ? 700 : 500,
                                      }}>
                                        {g}%
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {waccs.map(w => (
                                    <tr key={w} style={{ borderBottom: '1px solid #1e293b' }}>
                                      <td style={{
                                        padding: '8px 12px',
                                        fontWeight: w === baseWacc ? 700 : 500,
                                      }}>
                                        {w}%
                                      </td>
                                      {growths.map(g => {
                                        const fv = lookup.get(`${w}-${g}`);
                                        const isBase = w === baseWacc && g === baseGrowth;
                                        const isUpside = fv != null && currentPrice != null && fv >= currentPrice;
                                        return (
                                          <td key={g} style={{
                                            padding: '8px 12px',
                                            textAlign: 'right',
                                            fontWeight: isBase ? 700 : 400,
                                            backgroundColor: isBase ? 'rgba(139, 92, 246, 0.12)' :
                                              (fv != null && currentPrice != null
                                                ? (isUpside ? 'rgba(16, 185, 129, 0.06)' : 'rgba(239, 68, 68, 0.06)')
                                                : undefined),
                                            color: fv != null && currentPrice != null
                                              ? (isUpside ? '#10b981' : '#ef4444')
                                              : undefined,
                                            borderRadius: isBase ? '4px' : undefined,
                                          }}>
                                            {fv != null ? `${(research as any).trading_symbol || '$'}${fv.toFixed(2)}` : 'N/A'}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Box>
                          );
                        })()}
                      </Box>
                    )}

                    {/* DCF Narrative */}
                    {research.dcf_analysis.narrative && (
                      <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                        {research.dcf_analysis.narrative}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Financial Statements */}
              {research.financial_statements && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BarChartIcon sx={{ mr: 1, color: '#6366f1', fontSize: 28 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Financial Statements
                      </Typography>
                    </Box>

                    {/* Annual / Quarterly toggle */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                      <ToggleButtonGroup
                        value={fsView}
                        exclusive
                        onChange={(_, v) => { if (v) setFsView(v); }}
                        size="small"
                      >
                        <ToggleButton value="annual">Annual</ToggleButton>
                        <ToggleButton value="quarterly">Quarterly</ToggleButton>
                      </ToggleButtonGroup>

                      <ToggleButtonGroup
                        value={fsTab}
                        exclusive
                        onChange={(_, v) => { if (v) setFsTab(v); }}
                        size="small"
                      >
                        <ToggleButton value="income_statement">Income Statement</ToggleButton>
                        <ToggleButton value="balance_sheet">Balance Sheet</ToggleButton>
                        <ToggleButton value="cash_flow">Cash Flow</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {(() => {
                      const fsData = research.financial_statements?.[fsTab]?.[fsView];
                      if (!fsData || fsData.periods.length === 0 || fsData.rows.length === 0) {
                        return (
                          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                            No data available for this period.
                          </Typography>
                        );
                      }
                      return (
                        <TableContainer sx={{ maxHeight: 480 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, minWidth: 220, bgcolor: 'background.paper' }}>
                                  Metric
                                </TableCell>
                                {fsData.periods.map((p) => (
                                  <TableCell key={p} align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>
                                    {p}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {fsData.rows.map((row, idx) => (
                                <TableRow
                                  key={row.metric}
                                  sx={{
                                    bgcolor: idx % 2 === 0 ? 'action.hover' : 'transparent',
                                    '&:last-child td': { borderBottom: 0 },
                                  }}
                                >
                                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                    {row.metric}
                                  </TableCell>
                                  {row.values.map((v, vi) => (
                                    <TableCell
                                      key={vi}
                                      align="right"
                                      sx={{
                                        fontSize: '0.8rem',
                                        color: v && v.startsWith('-') ? 'error.main' : 'text.primary',
                                        fontVariantNumeric: 'tabular-nums',
                                      }}
                                    >
                                      {v ?? '—'}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}

              {/* Price Chart + Technical Analysis */}
              {research.chart_data && research.chart_data.length > 0 && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Price Chart &amp; Technical Analysis
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0 }}>
                        <FormControlLabel
                          control={<Checkbox checked={showMA20} onChange={(e) => setShowMA20(e.target.checked)} size="small" sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }} />}
                          label={<Typography variant="caption" sx={{ fontWeight: 600, color: '#ff9800' }}>20 MA</Typography>}
                        />
                        <FormControlLabel
                          control={<Checkbox checked={showMA50} onChange={(e) => setShowMA50(e.target.checked)} size="small" sx={{ color: '#f44336', '&.Mui-checked': { color: '#f44336' } }} />}
                          label={<Typography variant="caption" sx={{ fontWeight: 600, color: '#f44336' }}>50 MA</Typography>}
                        />
                        <FormControlLabel
                          control={<Checkbox checked={showMA200} onChange={(e) => setShowMA200(e.target.checked)} size="small" sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }} />}
                          label={<Typography variant="caption" sx={{ fontWeight: 600, color: '#9c27b0' }}>200 MA</Typography>}
                        />
                      </Box>
                    </Box>
                    <Box ref={chartRef} sx={{ height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={research.chart_data}>
                          <CartesianGrid strokeDasharray="3 3" stroke={bloomberg.chart.grid} />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          />
                          <YAxis
                            domain={['auto', 'auto']}
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => `${(research as any).trading_symbol || '$'}${Number(value).toFixed(2)}`}
                          />
                          <RechartsTooltip
                            formatter={(value: any, name: string) => {
                              const labels: Record<string, string> = { price: 'Price', ma_20: '20 MA', ma_50: '50 MA', ma_200: '200 MA' };
                              return [`${(research as any).trading_symbol || '$'}${Number(value).toFixed(2)}`, labels[name] || name];
                            }}
                            labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          />
                          <Line type="monotone" dataKey="price" stroke="#667eea" strokeWidth={2} dot={false} name="price" />
                          {showMA20 && <Line type="monotone" dataKey="ma_20" stroke="#ff9800" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="ma_20" connectNulls />}
                          {showMA50 && <Line type="monotone" dataKey="ma_50" stroke="#f44336" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="ma_50" connectNulls />}
                          {showMA200 && <Line type="monotone" dataKey="ma_200" stroke="#9c27b0" strokeWidth={1.5} strokeDasharray="6 3" dot={false} name="ma_200" connectNulls />}
                          {/* Crossover event markers */}
                          {research.technical_events?.map((evt, idx) => {
                            const pt = research.chart_data?.find(d => d.date === evt.date);
                            if (!pt) return null;
                            return (
                              <ReferenceDot
                                key={idx}
                                x={evt.date}
                                y={pt.price}
                                r={5}
                                fill={evt.sentiment === 'bullish' ? bloomberg.status.positive : bloomberg.status.negative}
                                stroke={bloomberg.bg.secondary}
                                strokeWidth={1.5}
                              />
                            );
                          })}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* Performance Indicators */}
                    {research.performance && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {([
                          ['1W', research.performance.week_1],
                          ['1M', research.performance.month_1],
                          ['3M', research.performance.month_3],
                          ['YTD', research.performance.ytd],
                          ['1Y', research.performance.year_1],
                        ] as [string, number | undefined][]).map(([label, val]) => (
                          val != null ? (
                            <Chip
                              key={label}
                              label={`${label}: ${val >= 0 ? '+' : ''}${val.toFixed(1)}%`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: val >= 0 ? bloomberg.status.positiveBg : bloomberg.status.negativeBg,
                                color: val >= 0 ? bloomberg.status.positive : bloomberg.status.negative,
                                border: 1,
                                borderColor: val >= 0 ? bloomberg.status.positive : bloomberg.status.negative,
                              }}
                            />
                          ) : null
                        ))}
                      </Box>
                    )}

                    {/* Technical Metrics */}
                    {research.technical_summary && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {(() => {
                          const ts = research.technical_summary;
                          type MetricItem = { label: string; sub: string; color: string; tooltip?: string };
                          const metrics: MetricItem[] = [];
                          if (ts.rsi != null) {
                            const c = ts.rsi > 70 ? bloomberg.status.negative : ts.rsi < 30 ? bloomberg.status.positive : bloomberg.text.secondary;
                            metrics.push({ label: `RSI: ${ts.rsi}`, sub: ts.rsi_label || '', color: c, tooltip: 'Relative Strength Index (14-day). Measures speed and magnitude of recent price changes. Above 70 = overbought, below 30 = oversold.' });
                          }
                          if (ts.momentum != null) {
                            const c = ts.momentum >= 0 ? bloomberg.status.positive : bloomberg.status.negative;
                            metrics.push({ label: `Momentum: ${ts.momentum >= 0 ? '+' : ''}${ts.momentum.toFixed(1)}%`, sub: '', color: c, tooltip: '10-day price momentum. Shows the percentage change over the last 10 trading days. Positive = upward trend, negative = downward trend.' });
                          }
                          if (ts.volatility != null) {
                            const c = ts.volatility > 40 ? bloomberg.status.negative : ts.volatility > 20 ? bloomberg.status.warning : bloomberg.status.positive;
                            metrics.push({ label: `HV (20d): ${ts.volatility.toFixed(1)}%`, sub: ts.volatility_label || '', color: c, tooltip: 'Historical Volatility (20-day annualized). Measures how much the stock price has actually fluctuated over the last 20 trading days, expressed as an annualized percentage. Calculated as the standard deviation of daily returns \u00d7 \u221a252.' });
                          }
                          if (ts.implied_volatility != null) {
                            const c = ts.implied_volatility > 50 ? bloomberg.status.negative : ts.implied_volatility > 25 ? bloomberg.status.warning : bloomberg.status.positive;
                            metrics.push({ label: `IV (30d): ${ts.implied_volatility.toFixed(1)}%`, sub: ts.iv_label || '', color: c, tooltip: 'Implied Volatility (30-day ATM). Derived from options pricing, reflects market expectations of future price movement. Calculated as the average of at-the-money call and put IV for the nearest monthly expiry. Higher IV = market expects bigger moves.' });
                          }
                          if (ts.price_vs_ma20 != null) metrics.push({ label: `vs 20MA: ${ts.price_vs_ma20 >= 0 ? '+' : ''}${ts.price_vs_ma20.toFixed(1)}%`, sub: '', color: ts.price_vs_ma20 >= 0 ? bloomberg.status.positive : bloomberg.status.negative });
                          if (ts.price_vs_ma50 != null) metrics.push({ label: `vs 50MA: ${ts.price_vs_ma50 >= 0 ? '+' : ''}${ts.price_vs_ma50.toFixed(1)}%`, sub: '', color: ts.price_vs_ma50 >= 0 ? bloomberg.status.positive : bloomberg.status.negative });
                          if (ts.price_vs_ma200 != null) metrics.push({ label: `vs 200MA: ${ts.price_vs_ma200 >= 0 ? '+' : ''}${ts.price_vs_ma200.toFixed(1)}%`, sub: '', color: ts.price_vs_ma200 >= 0 ? bloomberg.status.positive : bloomberg.status.negative });
                          return metrics.map((m, i) => {
                            const box = (
                              <Box key={i} sx={{ textAlign: 'center', p: 1, border: 1, borderColor: 'divider', borderRadius: 1, minWidth: 90, position: 'relative' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: m.color }}>{m.label}</Typography>
                                {m.sub && <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{m.sub}</Typography>}
                                {m.tooltip && (
                                  <InfoOutlinedIcon sx={{ position: 'absolute', top: 2, right: 2, fontSize: 13, color: 'text.disabled', cursor: 'pointer' }} />
                                )}
                              </Box>
                            );
                            return m.tooltip ? (
                              <MuiTooltip key={i} title={m.tooltip} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
                                {box}
                              </MuiTooltip>
                            ) : box;
                          });
                        })()}
                      </Box>
                    )}

                    {/* Technical Events */}
                    {research.technical_events && research.technical_events.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Recent Technical Events
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {research.technical_events.slice(0, 8).map((evt, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                              <Chip
                                label={evt.sentiment === 'bullish' ? 'Bullish' : 'Bearish'}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.65rem',
                                  height: 20,
                                  bgcolor: evt.sentiment === 'bullish' ? bloomberg.status.positiveBg : bloomberg.status.negativeBg,
                                  color: evt.sentiment === 'bullish' ? bloomberg.status.positive : bloomberg.status.negative,
                                }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                                {new Date(evt.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                {evt.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {evt.description}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Recent News */}
              {research.recent_news && research.recent_news.length > 0 && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <NewspaperIcon sx={{ mr: 1, color: '#3b82f6', fontSize: 28 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Recent News
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {research.recent_news.map((news, idx) => (
                        <Link 
                          key={idx}
                          href={news.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="inherit"
                        >
                          <Box 
                            sx={{ 
                              p: 2, 
                              bgcolor: 'background.default', 
                              borderRadius: 2,
                              transition: 'all 0.2s',
                              '&:hover': {
                                bgcolor: 'action.hover',
                                transform: 'translateX(4px)'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                                {news.title}
                                <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', opacity: 0.5 }} />
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              {news.source} • {new Date(news.published_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Link>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>

            {/* Sidebar */}
            <Box sx={{ pr: 0.5, minWidth: 0 }}>
              {/* Analyst Recommendations */}
              {research.analyst_recommendations?.has_data && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BarChartIcon sx={{ mr: 1, color: '#3b82f6', fontSize: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Analyst Ratings
                      </Typography>
                    </Box>
                    
                    {/* Consensus */}
                    {research.analyst_recommendations.consensus && (
                      <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                          Consensus
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {research.analyst_recommendations.consensus}
                        </Typography>
                        {research.analyst_recommendations.num_analysts && (
                          <Typography variant="caption" color="textSecondary">
                            Based on {research.analyst_recommendations.num_analysts} analyst{research.analyst_recommendations.num_analysts > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Trend Indicator */}
                    {research.analyst_recommendations.recommendation_trend && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        {research.analyst_recommendations.recommendation_trend === 'improving' && (
                          <>
                            <TrendingUpIcon sx={{ color: '#10b981' }} />
                            <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
                              Improving Sentiment
                            </Typography>
                          </>
                        )}
                        {research.analyst_recommendations.recommendation_trend === 'declining' && (
                          <>
                            <TrendingDownIcon sx={{ color: '#ef4444' }} />
                            <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 600 }}>
                              Declining Sentiment
                            </Typography>
                          </>
                        )}
                        {research.analyst_recommendations.recommendation_trend === 'stable' && (
                          <>
                            <TrendingFlatIcon sx={{ color: '#6b7280' }} />
                            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
                              Stable Sentiment
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                    
                    {/* Rating Breakdown */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ mb: 1, fontWeight: 600 }}>
                        Rating Breakdown
                      </Typography>
                      
                      {/* Strong Buy */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ width: 80, fontWeight: 500 }}>
                          Strong Buy
                        </Typography>
                        <Box sx={{ flex: 1, height: 20, bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              height: '100%', 
                              bgcolor: '#10b981',
                              width: `${(research.analyst_recommendations.breakdown.strong_buy / (research.analyst_recommendations.num_analysts || 1)) * 100}%`,
                              transition: 'width 0.3s'
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" sx={{ width: 30, textAlign: 'right', fontWeight: 600 }}>
                          {research.analyst_recommendations.breakdown.strong_buy}
                        </Typography>
                      </Box>

                      {/* Buy */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ width: 80, fontWeight: 500 }}>
                          Buy
                        </Typography>
                        <Box sx={{ flex: 1, height: 20, bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              height: '100%', 
                              bgcolor: '#34d399',
                              width: `${(research.analyst_recommendations.breakdown.buy / (research.analyst_recommendations.num_analysts || 1)) * 100}%`,
                              transition: 'width 0.3s'
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" sx={{ width: 30, textAlign: 'right', fontWeight: 600 }}>
                          {research.analyst_recommendations.breakdown.buy}
                        </Typography>
                      </Box>

                      {/* Hold */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ width: 80, fontWeight: 500 }}>
                          Hold
                        </Typography>
                        <Box sx={{ flex: 1, height: 20, bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              height: '100%', 
                              bgcolor: '#f59e0b',
                              width: `${(research.analyst_recommendations.breakdown.hold / (research.analyst_recommendations.num_analysts || 1)) * 100}%`,
                              transition: 'width 0.3s'
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" sx={{ width: 30, textAlign: 'right', fontWeight: 600 }}>
                          {research.analyst_recommendations.breakdown.hold}
                        </Typography>
                      </Box>

                      {/* Sell */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ width: 80, fontWeight: 500 }}>
                          Sell
                        </Typography>
                        <Box sx={{ flex: 1, height: 20, bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              height: '100%', 
                              bgcolor: '#f87171',
                              width: `${(research.analyst_recommendations.breakdown.sell / (research.analyst_recommendations.num_analysts || 1)) * 100}%`,
                              transition: 'width 0.3s'
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" sx={{ width: 30, textAlign: 'right', fontWeight: 600 }}>
                          {research.analyst_recommendations.breakdown.sell}
                        </Typography>
                      </Box>

                      {/* Strong Sell */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ width: 80, fontWeight: 500 }}>
                          Strong Sell
                        </Typography>
                        <Box sx={{ flex: 1, height: 20, bgcolor: 'background.default', borderRadius: 1, overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              height: '100%', 
                              bgcolor: '#ef4444',
                              width: `${(research.analyst_recommendations.breakdown.strong_sell / (research.analyst_recommendations.num_analysts || 1)) * 100}%`,
                              transition: 'width 0.3s'
                            }} 
                          />
                        </Box>
                        <Typography variant="caption" sx={{ width: 30, textAlign: 'right', fontWeight: 600 }}>
                          {research.analyst_recommendations.breakdown.strong_sell}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Price Targets */}
              {research.price_targets && research.price_targets.mean != null && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      12-Month Price Targets
                    </Typography>
                    
                    {/* Mean Target with Upside */}
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                        Average Target
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {(research as any).trading_symbol || '$'}{research.price_targets.mean.toFixed(2)}
                        </Typography>
                        {research.price_targets.upside !== null && research.price_targets.upside !== undefined && (
                          <Chip
                            size="small"
                            label={`${research.price_targets.upside >= 0 ? '+' : ''}${research.price_targets.upside.toFixed(1)}%`}
                            color={research.price_targets.upside >= 0 ? 'success' : 'error'}
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      {research.price_targets.num_analysts && (
                        <Typography variant="caption" color="textSecondary">
                          {research.price_targets.num_analysts} analyst{research.price_targets.num_analysts > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>

                    {/* High/Low Range */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {research.price_targets.high && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="textSecondary">High Target</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#10b981' }}>
                            {(research as any).trading_symbol || '$'}{research.price_targets.high.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      {research.price_targets.low && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="textSecondary">Low Target</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                            {(research as any).trading_symbol || '$'}{research.price_targets.low.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      {research.current_price && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="textSecondary">Current Price</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {(research as any).trading_symbol || '$'}{research.current_price.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Analyst Ratings — per firm breakdown */}
              {(analystTargetsLoading || analystTargets.length > 0) && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Analyst Ratings
                    </Typography>
                    {analystTargetsLoading ? (
                      <Typography variant="caption" color="text.secondary">Loading…</Typography>
                    ) : (
                      <Box sx={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                              {['Date', 'Firm', 'Action', 'Rating', 'From', 'To'].map(h => (
                                <th key={h} style={{ textAlign: 'left', padding: '4px 10px 6px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', fontSize: 11 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {analystTargets.map((t, i) => {
                              const cat = t.category?.toLowerCase() ?? '';
                              const actionColor =
                                cat === 'upgrade'   ? '#4caf50' :
                                cat === 'downgrade' ? '#ef5350' :
                                cat === 'initiated' ? '#90caf9' : 'rgba(255,255,255,0.5)';
                              const sym = (research as any)?.trading_symbol || '$';
                              return (
                                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                                  <td style={{ padding: '5px 10px', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>{t.date}</td>
                                  <td style={{ padding: '5px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{t.analyst}</td>
                                  <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                                    <span style={{ color: actionColor, fontWeight: 600, fontSize: 11 }}>{t.category}</span>
                                  </td>
                                  <td style={{ padding: '5px 10px' }}>{t.rating}</td>
                                  <td style={{ padding: '5px 10px', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
                                    {t.target_from != null ? `${sym}${t.target_from.toFixed(0)}` : '—'}
                                  </td>
                                  <td style={{ padding: '5px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    {t.target_to != null ? `${sym}${t.target_to.toFixed(0)}` : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Fundamentals */}
              {research.fundamentals?.has_data && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                      Fundamentals
                    </Typography>
                    
                    {/* Valuation */}
                    {(research.fundamentals.valuation.pe_ratio || research.fundamentals.valuation.price_to_book) && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                          Valuation
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                          {research.fundamentals.valuation.pe_ratio && typeof research.fundamentals.valuation.pe_ratio === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">P/E Ratio</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {research.fundamentals.valuation.pe_ratio.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.valuation.forward_pe && typeof research.fundamentals.valuation.forward_pe === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">Forward P/E</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {research.fundamentals.valuation.forward_pe.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.valuation.peg_ratio && typeof research.fundamentals.valuation.peg_ratio === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">PEG Ratio</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {research.fundamentals.valuation.peg_ratio.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.valuation.price_to_book && typeof research.fundamentals.valuation.price_to_book === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">P/B Ratio</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {research.fundamentals.valuation.price_to_book.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.valuation.price_to_sales && typeof research.fundamentals.valuation.price_to_sales === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">P/S Ratio</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {research.fundamentals.valuation.price_to_sales.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {/* Profitability */}
                    {(research.fundamentals.profitability.profit_margin || research.fundamentals.profitability.roe) && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                          Profitability
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                          {research.fundamentals.profitability.profit_margin && typeof research.fundamentals.profitability.profit_margin === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">Net Margin</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                                {(research.fundamentals.profitability.profit_margin * 100).toFixed(2)}%
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.profitability.operating_margin && typeof research.fundamentals.profitability.operating_margin === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">Operating Margin</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                                {(research.fundamentals.profitability.operating_margin * 100).toFixed(2)}%
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.profitability.gross_margin && typeof research.fundamentals.profitability.gross_margin === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">Gross Margin</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                                {(research.fundamentals.profitability.gross_margin * 100).toFixed(2)}%
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.profitability.roe && typeof research.fundamentals.profitability.roe === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">ROE</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                                {(research.fundamentals.profitability.roe * 100).toFixed(2)}%
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.profitability.roa && typeof research.fundamentals.profitability.roa === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">ROA</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981' }}>
                                {(research.fundamentals.profitability.roa * 100).toFixed(2)}%
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {/* Growth */}
                    {(research.fundamentals.growth.revenue_growth || research.fundamentals.growth.earnings_growth) && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                          Growth
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                          {research.fundamentals.growth.revenue_growth && typeof research.fundamentals.growth.revenue_growth === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">Revenue Growth</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: research.fundamentals.growth.revenue_growth >= 0 ? '#10b981' : '#ef4444' }}>
                                {(research.fundamentals.growth.revenue_growth * 100).toFixed(2)}%
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.growth.earnings_growth && typeof research.fundamentals.growth.earnings_growth === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">Earnings Growth</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: research.fundamentals.growth.earnings_growth >= 0 ? '#10b981' : '#ef4444' }}>
                                {(research.fundamentals.growth.earnings_growth * 100).toFixed(2)}%
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.growth.eps_ttm && typeof research.fundamentals.growth.eps_ttm === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">EPS (TTM)</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                ${research.fundamentals.growth.eps_ttm.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.growth.eps_forward && typeof research.fundamentals.growth.eps_forward === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">EPS (Forward)</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                ${research.fundamentals.growth.eps_forward.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}

                    {/* Financial Health */}
                    {(research.fundamentals.financial_health.current_ratio || research.fundamentals.financial_health.debt_to_equity) && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                          Financial Health
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {research.fundamentals.financial_health.current_ratio && typeof research.fundamentals.financial_health.current_ratio === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">Current Ratio</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {research.fundamentals.financial_health.current_ratio.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                          {research.fundamentals.financial_health.debt_to_equity && typeof research.fundamentals.financial_health.debt_to_equity === 'number' && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" color="textSecondary">Debt/Equity</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {research.fundamentals.financial_health.debt_to_equity.toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Key Stats */}
              {research.key_stats && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Key Statistics
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {research.key_stats.market_cap && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">Market Cap</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {research.key_stats.market_cap}
                          </Typography>
                        </Box>
                      )}
                      {research.key_stats.pe_ratio && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">P/E Ratio</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {research.key_stats.pe_ratio}
                          </Typography>
                        </Box>
                      )}
                      {research.key_stats.dividend_yield && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">Dividend Yield</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {research.key_stats.dividend_yield}
                          </Typography>
                        </Box>
                      )}
                      {research.key_stats.week_52_high && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">52 Week High</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            ${research.key_stats.week_52_high.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      {research.key_stats.week_52_low && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">52 Week Low</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            ${research.key_stats.week_52_low.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      {research.key_stats.volume && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">Avg Volume</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {research.key_stats.volume}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Upcoming Events */}
              {research.upcoming_events && research.upcoming_events.length > 0 && (
                <Card sx={{ mb: 1.5 }} elevation={3}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarTodayIcon sx={{ mr: 1, color: '#8b5cf6', fontSize: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Upcoming Events
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {research.upcoming_events.map((event, idx) => (
                        <Box 
                          key={idx}
                          sx={{ 
                            p: 2, 
                            bgcolor: 'background.default', 
                            borderRadius: 2,
                            borderLeft: '3px solid #8b5cf6'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {event.event}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {event.date}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* AI Recommendation */}
              {research.recommendation && (
                <Card 
                  sx={{
                    mb: 3,
                    border: '2px solid',
                    borderColor: research.recommendation.rating === 'BUY'
                      ? 'success.main'
                      : research.recommendation.rating === 'SELL'
                      ? 'error.main'
                      : 'warning.main',
                  }}
                  elevation={3}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      AI Recommendation
                    </Typography>
                    <Typography variant="h4" sx={{
                      fontWeight: 700, mb: 2,
                      color: research.recommendation.rating === 'BUY' ? 'success.main'
                        : research.recommendation.rating === 'SELL' ? 'error.main'
                        : 'warning.main'
                    }}>
                      {research.recommendation.rating}
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                      {research.recommendation.summary}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Disclaimer */}
              <Card sx={{ bgcolor: 'background.default' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.5 }}>
                    This research is AI-generated and for informational purposes only. Not financial advice. 
                    Always conduct your own research and consult with a financial advisor before making investment decisions.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      )}

      {/* Empty State */}
      {!loading && !research && !error && (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.default' }} elevation={0}>
          <SearchIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
            Start Your Research
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Enter a ticker symbol above to generate comprehensive AI-powered research and insights
          </Typography>
        </Paper>
      )}

      {/* Action Snackbar */}
      <Snackbar
        open={!!actionSnackbar}
        autoHideDuration={2500}
        onClose={() => setActionSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setActionSnackbar(null)} sx={{ fontSize: '0.8rem' }}>
          {actionSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Research;
